/* =====================================================================
 * AppRouter — 전체 라우트 트리 (공개 + 인증 라우트)
 *
 * react-router-dom v6의 <Routes> 기반으로 구성합니다.
 * - 공개 라우트: /auth/callback, /pricing, /terms 등
 * - 인증 라우트: AuthGuard로 감싸며 Layout → WorkspaceShell 렌더링
 *
 * 새 모드 추가 시 routeConfig.ts에만 추가하면 자동 등록됩니다.
 * ===================================================================== */

import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AppSkeleton } from '@/components/common/Skeleton';
import { AuthGuard } from './AuthGuard';
import { SEOHead } from './SEOHead';
import { ROUTE_ENTRIES } from './routeConfig';
import { AuthCallbackPage } from '@/components/auth/AuthCallbackPage';
import { hasAuthToken } from '@/api/authFetch';
import { getActiveAuthAccount } from '@/api/authVault';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import i18n from '@/i18n';

const SKELETON_DELAY_MS = 500;

const DelayedAppSkeleton: React.FC = () => {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const suppressSkeleton = location.pathname === '/auth/callback'
    || (location.pathname === '/widgets' && (hasAuthToken() || !!getActiveAuthAccount()));
  useEffect(() => {
    if (suppressSkeleton) {
      setShow(false);
      return undefined;
    }
    const id = window.setTimeout(() => setShow(true), SKELETON_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [suppressSkeleton]);
  if (suppressSkeleton) return null;
  if (!show) return null;
  return <AppSkeleton />;
};

/* ---- Lazy Load Components ---- */
const TermsPage = lazyWithRetry(() => import('@/components/legal/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazyWithRetry(() => import('@/components/legal/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const MarketingConsentPage = lazyWithRetry(() => import('@/components/legal/MarketingConsentPage').then(m => ({ default: m.MarketingConsentPage })));
const PricingPage = lazyWithRetry(() => import('@/components/pricing/PricingPage').then(m => ({ default: m.PricingPage })));
const ContactSalesPage = lazyWithRetry(() => import('@/components/pricing/ContactSalesPage').then(m => ({ default: m.ContactSalesPage })));
const PaddleCheckoutPage = lazyWithRetry(() => import('@/components/pricing/PaddleCheckoutPage').then(m => ({ default: m.PaddleCheckoutPage })));
const SharedWorkspacePage = lazyWithRetry(() => import('@/components/share/SharedWorkspacePage').then(m => ({ default: m.SharedWorkspacePage })));
const Layout = lazyWithRetry(() => import('@/components/layout/Layout').then(m => ({ default: m.Layout })));

/* ------------------------------------------------------------------ */
/* AuthenticatedLayout — Layout + SEOHead를 감싸는 래퍼                   */
/* ------------------------------------------------------------------ */

const AuthenticatedLayout: React.FC = () => (
  <>
    <SEOHead />
    <Layout />
  </>
);

// /invite/team/:token 상태에서 이미 로그인된 사용자는
// 토큰을 query로 보존한 채 /agents로 이동시켜 App의 초대 수락 effect가 정상 동작하게 한다.
const InviteAuthedRedirect: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const nextPath = token
    ? `/agents?teamInvite=${encodeURIComponent(token)}`
    : '/agents';
  return <Navigate to={nextPath} replace />;
};

// 레거시 결제 경로(/paddle-checkout)로 들어와도 _ptxn 쿼리를 유지해야
// Paddle checkout transaction link가 정상 동작합니다.
const LegacyCheckoutRedirect: React.FC = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/checkout',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

/* ------------------------------------------------------------------ */
/* Props                                                               */
/* ------------------------------------------------------------------ */

interface AppRouterProps {
  isAuthed: boolean;
  authChecking: boolean;
  inviteToken: string;
  onAuthed: () => Promise<void> | void;
}

/* ------------------------------------------------------------------ */
/* Router                                                              */
/* ------------------------------------------------------------------ */

export const AppRouter: React.FC<AppRouterProps> = ({
  isAuthed,
  authChecking,
  inviteToken,
  onAuthed,
}) => {
  return (
    <Suspense fallback={<DelayedAppSkeleton />}>
      <Routes>
        {/* ---- 공개 라우트 ---- */}
        <Route
          path="/auth/callback"
          element={
            <>
              <SEOHead title={i18n.t('auth:callback.processing')} />
              <AuthCallbackPage onAuthed={onAuthed} />
            </>
          }
        />
        <Route path="/share/:token" element={<SharedWorkspacePage />} />
        <Route
          path="/terms"
          element={
            <>
              <SEOHead />
              <TermsPage />
            </>
          }
        />
        <Route
          path="/privacy"
          element={
            <>
              <SEOHead />
              <PrivacyPage />
            </>
          }
        />
        <Route
          path="/marketing-consent"
          element={
            <>
              <SEOHead />
              <MarketingConsentPage />
            </>
          }
        />
        <Route
          path="/pricing"
          element={
            <>
              <SEOHead />
              <PricingPage />
            </>
          }
        />
        <Route
          path="/checkout"
          element={
            <>
              <SEOHead />
              <PaddleCheckoutPage />
            </>
          }
        />
        {/* 기존 경로 하위 호환 */}
        <Route path="/paddle-checkout" element={<LegacyCheckoutRedirect />} />
        <Route
          path="/contact-sales"
          element={
            <>
              <SEOHead />
              <ContactSalesPage />
            </>
          }
        />

        {/* ---- 팀 초대 (공개) ---- */}
        <Route
          path="/invite/team/:token"
          element={
            <AuthGuard
              isAuthed={isAuthed}
              authChecking={authChecking}
              inviteToken={inviteToken}
            />
          }
        >
          <Route index element={<InviteAuthedRedirect />} />
        </Route>

        {/* ---- 인증 라우트 (AuthGuard + Layout) ---- */}
        <Route
          element={
            <AuthGuard
              isAuthed={isAuthed}
              authChecking={authChecking}
              inviteToken={inviteToken}
            />
          }
        >
          {/* 인증된 사용자 → Layout 렌더링 */}
          <Route element={<AuthenticatedLayout />}>
            {/* routeConfig에서 자동 생성 */}
            {ROUTE_ENTRIES.map((entry) => (
              <React.Fragment key={entry.path}>
                <Route path={entry.path} element={null} />
                {entry.hasIdParam && (
                  <Route path={`${entry.path}/:id`} element={null} />
                )}
              </React.Fragment>
            ))}

            {/* /settings 하위 리다이렉트 */}
            <Route path="/settings" element={<Navigate to="/settings/team" replace />} />
          </Route>

          {/* 루트 → /widgets 리다이렉트 */}
          <Route path="/" element={<Navigate to="/widgets" replace />} />
        </Route>

        {/* ---- 404 폴백 → /widgets ---- */}
        <Route path="*" element={<Navigate to="/widgets" replace />} />
      </Routes>
    </Suspense>
  );
};
